import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { LeadSettings } from "../../../../components/Modal/Lead/LeadSettings";
import { useParams } from "react-router-dom";
import { Notification } from "../../../../components/UI/Notifications";
import { fieldsConfigAdmin, fieldsConfigAffiliator } from "../../../../utils/forms/leadFields";
import { getLeadValueByKey } from "../../../../utils/forms/getLeadValueByKey";
import { getLeadById } from "../../../../utils/api/Lead/getLeadById";
import { updateLeadTradingAccess } from "../../../../utils/api/Lead/updateLeadTradingAccess";
import { startLeadCall } from "../../../../utils/api/Lead/startLeadCall";
import { LoadingCircle } from "../../../../components/UI/Custom/LoadingCircle";
import styles from "../../../../styles/pages/Lead/LeadManagement/Info.module.css";
import { handleError } from "../../../../utils/custom/errorHandler";
import { formatDateTime } from "../../../../utils/custom/formatters";
import { LangContext } from "../../../../context/LangContext";
import { ThemeContext } from "../../../../context/ThemeContext";
import { fieldLabels } from "../../../../utils/forms/fieldsLabels";
import { getGeoCountryName, getGeoFlagPath } from "../../../../utils/geoCountryNames";
import { getLeadFieldAccessMode, LEAD_FIELD_ACCESS_MODE } from "../../../../utils/leadFieldAccess";

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { KycSalesData } from './InfoComponents/KycSalesData';
import { KycRetentionData } from './InfoComponents/KycRetentionData';

const translations = {
    ru: {
        copyableMessage: (label) => `Значение поля "${label}" скопировано в буфер обмена.`,
        callStartingMessage: "Запуск звонка...",
        callStartedMessage: "Звонок запущен.",
        callFailedMessage: "Не удалось запустить звонок.",
        callUnavailableMessage: "Звонки через CRM недоступны для этой учетной записи.",
        loadingError: "Ошибка загрузки данных.",
        tryAgain: "Пожалуйста, попробуйте обновить страницу.",
        noData: "Нет данных для отображения.",
        tabGeneral: "Общая информация",
        tabConfig: "Конфиг",
        tabKycSales: "KYC Продажи",
        tabKycRetention: "KYC Удержание",
        show: "Показать",
        hide: "Скрыть",
        tradingAccess: "Доступ к торговле",
        tradingAccessAllowed: "Разрешен",
        tradingAccessBlocked: "Заблокирован",
        tradingAccessUpdateError: "Не удалось обновить доступ к торговле.",
        call: "Позвонить",
        callStarting: "Запуск звонка",
        hidden: "Скрыто",
    },
    en: {
        copyableMessage: (label) => `Value of field "${label}" copied to clipboard.`,
        callStartingMessage: "Starting call...",
        callStartedMessage: "Call started.",
        callFailedMessage: "Failed to start call.",
        callUnavailableMessage: "CRM calling is unavailable for this account.",
        loadingError: "Data loading error.",
        tryAgain: "Please try refreshing the page.",
        noData: "No data to display.",
        tabGeneral: "General Info",
        tabConfig: "Config",
        tabKycSales: "KYC Sales",
        tabKycRetention: "KYC Retention",
        show: "Show",
        hide: "Hide",
        tradingAccess: "Trading Access",
        tradingAccessAllowed: "Allowed",
        tradingAccessBlocked: "Blocked",
        tradingAccessUpdateError: "Failed to update trading access.",
        call: "Call",
        callStarting: "Starting call",
        hidden: "Hidden",
    },
};

const CALLABLE_PHONE_FIELDS = new Set(['phone', 'second_phone']);
const hasPhoneValue = (phoneNumber) => {
    if (phoneNumber === null || phoneNumber === undefined) {
        return false;
    }

    return String(phoneNumber).trim() !== '';
};

const getMaskedPhoneValue = (phoneNumber) => {
    if (!hasPhoneValue(phoneNumber)) {
        return '***';
    }

    const normalizedPhone = String(phoneNumber).trim();

    if (normalizedPhone.length <= 4) {
        return '*'.repeat(normalizedPhone.length);
    }

    return `${normalizedPhone.slice(0, 2)}${'*'.repeat(normalizedPhone.length - 4)}${normalizedPhone.slice(-2)}`;
};

const groupFields = (config) => {
    const groups = {
        general: [],
        config: [],
        kycSales: [],
        kycRetention: [],
    };
    const configKeys = new Set(['bot_profit_percent']);
    const hiddenKeys = new Set(['ai_trading']);
    const renderedKeys = new Set();

    config.forEach(field => {
        if (hiddenKeys.has(field.key) || renderedKeys.has(field.key)) {
            return;
        }

        if (field.key.startsWith('kyc_sales_')) {
            groups.kycSales.push(field);
        } else if (field.key.startsWith('kyc_retention_')) {
            groups.kycRetention.push(field);
        } else if (configKeys.has(field.key) && field.detailsStatus === 'active') {
            groups.config.push(field);
        } else if (field.detailsStatus === 'active') {
            groups.general.push(field);
        }

        renderedKeys.add(field.key);
    });

    return groups;
};

const getKycStatus = (fields, leadData) => {
    if (!fields || fields.length === 0) {
        return 'empty';
    }

    let totalFields = fields.length;
    let filledCount = 0;

    fields.forEach(field => {
        const value = getLeadValueByKey(leadData, field.key);
        if (value !== null && value !== undefined && value !== '') {
            filledCount++;
        }
    });

    if (filledCount === totalFields) {
        return 'full';
    } else if (filledCount > 0) {
        return 'partial';
    } else {
        return 'empty';
    }
};

export const Info = ({ role }) => {
    const { id } = useParams();
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;
    const currentFieldLabels = fieldLabels[langSelected] || fieldLabels.ru;

    const [leadData, setLeadData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [isError, setIsError] = useState(false);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [modalData, setModalData] = useState({
        key: "",
        label: "",
        value: "",
        id: id
    });
    const [copyableMessage, setCopyableMessage] = useState("");
    const [callNotification, setCallNotification] = useState(null);
    const [activeCallPhoneField, setActiveCallPhoneField] = useState(null);
    const [fieldsConfig, setFieldsConfig] = useState([]);

    const [activeTab, setActiveTab] = useState('general');

    const [showPassword, setShowPassword] = useState(false);
    const [isTradingAccessSubmitting, setIsTradingAccessSubmitting] = useState(false);
    const tradingAccessMode = role === 'admin'
        ? getLeadFieldAccessMode(leadData?.field_access_modes, 'trading_access_blocked')
        : LEAD_FIELD_ACCESS_MODE.EDIT;
    const canViewTradingAccess = role === 'admin' && tradingAccessMode !== LEAD_FIELD_ACCESS_MODE.HIDE;
    const canEditTradingAccess = role === 'admin' && tradingAccessMode === LEAD_FIELD_ACCESS_MODE.EDIT;


    useEffect(() => {
        if (role === 'admin') {
            setFieldsConfig(fieldsConfigAdmin);
        } else if (role === 'affiliator') {
            setFieldsConfig(fieldsConfigAffiliator);
        } else {
            setFieldsConfig([]);
        }
    }, [role]);

    useEffect(() => {
        if (copyableMessage) {
            const timer = setTimeout(() => {
                setCopyableMessage('');
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [copyableMessage]);

    const handleCopyable = (label, value) => {
        if (typeof navigator === "undefined" || !value) return;

        setCopyableMessage(currentTranslation.copyableMessage(label));
        navigator.clipboard?.writeText(String(value));
    };

    const loadLeadData = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);
        setErrors({});
        try {
            const data = await getLeadById(id, role);
            setLeadData(data);
            setIsLoading(false);
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
            setIsError(true);
            setLeadData(null);
            setIsLoading(false);
        }
    }, [id, role]);

    useEffect(() => {
        loadLeadData();
    }, [loadLeadData]);

    const handleSaveSuccess = useCallback(() => {
        loadLeadData();
    }, [loadLeadData]);

    const handleTradingAccessToggle = useCallback(async () => {
        if (role !== 'admin' || !leadData || isTradingAccessSubmitting || !canEditTradingAccess) {
            return;
        }

        const nextBlocked = !Boolean(leadData.trading_access_blocked);
        setIsTradingAccessSubmitting(true);

        try {
            const updatedLead = await updateLeadTradingAccess(id, nextBlocked);
            setLeadData(updatedLead);
            setErrors((prev) => {
                if (!prev.trading_access) {
                    return prev;
                }

                const next = { ...prev };
                delete next.trading_access;
                return next;
            });
        } catch (error) {
            const nextError = error?.errors?.trading_access_blocked?.[0]
                || error?.errors?.[0]?.message
                || error?.message
                || currentTranslation.tradingAccessUpdateError;

            setErrors((prev) => ({
                ...prev,
                trading_access: nextError,
            }));
        } finally {
            setIsTradingAccessSubmitting(false);
        }
    }, [canEditTradingAccess, currentTranslation.tradingAccessUpdateError, id, isTradingAccessSubmitting, leadData, role]);

    const getDisplayValue = useCallback((key, raw) => {
        const isMissing = raw === null || raw === undefined || raw === '';

        if (key === 'bank_liquidity') {
            return isMissing ? 0 : String(raw);
        }

        if (key === 'password' && !isMissing && !showPassword) {
            return '**********';
        }

        return isMissing ? 'N/A' : raw;
    }, [showPassword]);

    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const showCallNotification = useCallback((message, type = "info") => {
        setCallNotification({
            id: Date.now(),
            message,
            type,
        });
    }, []);

    const handleCallClick = useCallback(async (phoneField) => {
        if (activeCallPhoneField) {
            return;
        }

        if (role !== 'admin') {
            showCallNotification(currentTranslation.callUnavailableMessage, "error");
            return;
        }

        setActiveCallPhoneField(phoneField);
        showCallNotification(currentTranslation.callStartingMessage, "info");

        try {
            await startLeadCall(id, phoneField);
            showCallNotification(currentTranslation.callStartedMessage, "info");
        } catch (error) {
            const message = error?.message && error.message.trim()
                ? error.message
                : currentTranslation.callFailedMessage;

            showCallNotification(message, "error");
        } finally {
            setActiveCallPhoneField(null);
        }
    }, [
        activeCallPhoneField,
        currentTranslation.callFailedMessage,
        currentTranslation.callStartedMessage,
        currentTranslation.callStartingMessage,
        currentTranslation.callUnavailableMessage,
        id,
        role,
        showCallNotification,
    ]);

    const renderField = (fieldConfig) => {
        const { key, label, detailsStatus } = fieldConfig;
        let value = getLeadValueByKey(leadData, key);
        const fieldAccessMode = getLeadFieldAccessMode(leadData?.field_access_modes, key);
        const isPhoneField = CALLABLE_PHONE_FIELDS.has(key);
        const canViewField = fieldAccessMode !== LEAD_FIELD_ACCESS_MODE.HIDE;
        const isHiddenPhoneField = isPhoneField && fieldAccessMode === LEAD_FIELD_ACCESS_MODE.HIDE;
        const isEditable = detailsStatus === 'active' && fieldAccessMode === LEAD_FIELD_ACCESS_MODE.EDIT;
        const isCopyable = fieldAccessMode === LEAD_FIELD_ACCESS_MODE.VIEW
            || (detailsStatus === 'active' && fieldAccessMode === LEAD_FIELD_ACCESS_MODE.EDIT);
        const isPassword = key === 'password';

        if (key === 'created_at' || key === 'updated_at' || key === 'last_accessed_at' || key === 'last_commented_at') {
            value = formatDateTime(value);
        }

        const rawValue = getLeadValueByKey(leadData, key);
        const callablePhoneValue = key === 'phone'
            ? (leadData?.call_phone ?? rawValue)
            : key === 'second_phone'
                ? (leadData?.call_second_phone ?? rawValue)
                : rawValue;
        const displayValue = isHiddenPhoneField
            ? getMaskedPhoneValue(callablePhoneValue)
            : canViewField
                ? getDisplayValue(key, value)
                : currentTranslation.hidden;
        const translatedLabel = currentFieldLabels[key] || label;
        const infoItemClass = `${styles.infoItem} ${theme === 'dark' ? styles.darkInfoItem : ''}`;
        const displayValueClass = `${styles.displayValue} ${theme === 'dark' ? styles.darkDisplayValue : ''}`;
        const labelBoxClass = `${styles.labelBox} ${theme === 'dark' ? styles.darkLabelBox : ''}`;
        const canShowCallAction = isPhoneField && hasPhoneValue(callablePhoneValue) && (canViewField || isHiddenPhoneField);
        const isCallSubmitting = activeCallPhoneField === key;
        const geoName = key === 'geo' ? getGeoCountryName(rawValue, langSelected) : '';
        const geoFlagPath = key === 'geo' ? getGeoFlagPath(rawValue) : null;
        const displayContent = canViewField && key === 'geo' && geoName
            ? (
                <span className={styles.geoValue}>
                    {geoFlagPath && (
                        <img
                            src={geoFlagPath}
                            alt={geoName}
                            className={styles.geoFlag}
                            loading="lazy"
                        />
                    )}
                    <span className={displayValueClass}>{geoName}</span>
                </span>
            )
            : <span className={displayValueClass}>{displayValue}</span>;

        return (
            <div className={infoItemClass} key={key}>
                <div className={labelBoxClass}>{translatedLabel}</div>
                <div className={styles.displayBox}>
                    {displayContent}
                </div>
                <div className={styles.actionBox}>
                    {canShowCallAction && (
                        <button
                            type="button"
                            className={`${styles.callIcon} ${isCallSubmitting ? styles.callIconSubmitting : ''} ${theme === 'dark' ? styles.darkCallIcon : ''}`}
                            title={isCallSubmitting ? currentTranslation.callStarting : currentTranslation.call}
                            aria-label={isCallSubmitting ? currentTranslation.callStarting : currentTranslation.call}
                            aria-busy={isCallSubmitting}
                            disabled={activeCallPhoneField !== null}
                            onClick={() => handleCallClick(key)}
                        >
                            <LocalPhoneIcon />
                        </button>
                    )}
                    {isPassword && rawValue && fieldAccessMode === LEAD_FIELD_ACCESS_MODE.EDIT && (
                        <div
                            className={`${styles.copyIcon} ${theme === 'dark' ? styles.darkCopyIcon : ''}`}
                            onClick={togglePasswordVisibility}
                            title={showPassword ? currentTranslation.hide : currentTranslation.show}
                        >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </div>
                    )}

                    {isEditable && (
                        <div
                            className={`${styles.editIcon} ${theme === 'dark' ? styles.darkEditIcon : ''}`}
                            onClick={() => {
                                const modalValue = getLeadValueByKey(leadData, key);
                                setModalData({
                                    key,
                                    label: translatedLabel,
                                    value: modalValue,
                                    id
                                });
                                setIsOpenModal(true);
                            }}
                        >
                            <EditIcon />
                        </div>
                    )}
                    {isCopyable && (
                        <div className={`${styles.copyIcon} ${theme === 'dark' ? styles.darkCopyIcon : ''}`} onClick={() => handleCopyable(translatedLabel, rawValue)}>
                            <ContentCopyIcon />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const translatedFieldsConfig = useMemo(() => {
        return fieldsConfig
            .filter(field => field.detailsStatus === 'active' || field.key.startsWith('kyc_'))
            .map(field => ({
                ...field,
                label: currentFieldLabels[field.key] || field.label
            }));
    }, [fieldsConfig, currentFieldLabels]);

    const groupedFields = useMemo(() => groupFields(translatedFieldsConfig), [translatedFieldsConfig]);

    const kycSalesStatus = useMemo(() => getKycStatus(groupedFields.kycSales, leadData), [groupedFields.kycSales, leadData]);
    const kycRetentionStatus = useMemo(() => getKycStatus(groupedFields.kycRetention, leadData), [groupedFields.kycRetention, leadData]);


    if (isLoading) {
        return (
            <div className={`${styles.loadingContainer} ${theme === 'dark' ? styles.darkLoadingContainer : ''}`}>
                <LoadingCircle size={40} thickness={3} />
            </div>
        );
    }

    if (isError) {
        return (
            <div className={`${styles.errorMessage} ${theme === 'dark' ? styles.darkErrorMessage : ''}`}>
                <p>{currentTranslation.loadingError}</p>
                <p>{errors.general || currentTranslation.tryAgain}</p>
            </div>
        );
    }

    if (!leadData) {
        return <div className={`${styles.noDataMessage} ${theme === 'dark' ? styles.darkNoDataMessage : ''}`}>{currentTranslation.noData}</div>;
    }

    const hasKycSalesFields = groupedFields.kycSales.length > 0;
    const hasKycRetentionFields = groupedFields.kycRetention.length > 0;
    const shouldShowTradingAccessField = canViewTradingAccess;
    const hasConfigFields = groupedFields.config.length > 0;
    const hasConfigSection = hasConfigFields || shouldShowTradingAccessField;

    const infoPageWrapperClass = `${styles.infoPageWrapper} ${theme === 'dark' ? styles.darkInfoPageWrapper : ''}`;
    const tabsHeaderClass = `${styles.tabsHeader} ${theme === 'dark' ? styles.darkTabsHeader : ''}`;
    const infoGridClass = `${styles.infoGrid} ${theme === 'dark' ? styles.darkInfoGrid : ''}`;

    return (
        <div className={infoPageWrapperClass}>
            {copyableMessage && <Notification message={copyableMessage} type={"info"} />}
            {callNotification && <Notification key={callNotification.id} message={callNotification.message} type={callNotification.type} />}

            <div className={tabsHeaderClass}>
                <div
                    className={`${styles.tabButton} ${activeTab === 'general' ? styles.tabActive : ''} ${theme === 'dark' ? styles.darkTabButton : ''} ${activeTab === 'general' ? styles.darkTabActive : ''}`}
                    onClick={() => setActiveTab('general')}
                >
                    {currentTranslation.tabGeneral}
                </div>

                {hasKycSalesFields && (
                    <div
                        className={`${styles.tabButton} ${activeTab === 'kycSales' ? styles.tabActive : ''} ${styles[`kycStatus-${kycSalesStatus}`]} ${theme === 'dark' ? styles.darkTabButton : ''} ${activeTab === 'kycSales' ? styles.darkTabActive : ''} ${styles[`darkKycStatus-${kycSalesStatus}`]}`}
                        onClick={() => setActiveTab('kycSales')}
                    >
                        {currentTranslation.tabKycSales}
                    </div>
                )}

                {hasKycRetentionFields && (
                    <div
                        className={`${styles.tabButton} ${activeTab === 'kycRetention' ? styles.tabActive : ''} ${styles[`kycStatus-${kycRetentionStatus}`]} ${theme === 'dark' ? styles.darkTabButton : ''} ${activeTab === 'kycRetention' ? styles.darkTabActive : ''} ${styles[`darkKycStatus-${kycRetentionStatus}`]}`}
                        onClick={() => setActiveTab('kycRetention')}
                    >
                        {currentTranslation.tabKycRetention}
                    </div>
                )}

                {hasConfigSection && (
                    <div
                        className={`${styles.tabButton} ${activeTab === 'config' ? styles.tabActive : ''} ${theme === 'dark' ? styles.darkTabButton : ''} ${activeTab === 'config' ? styles.darkTabActive : ''}`}
                        onClick={() => setActiveTab('config')}
                    >
                        {currentTranslation.tabConfig}
                    </div>
                )}
            </div>


            {activeTab === 'general' && (
                <div className={infoGridClass}>
                    {groupedFields.general.map(renderField)}
                </div>
            )}

            {activeTab === 'config' && hasConfigSection && (
                <div className={infoGridClass}>
                    {shouldShowTradingAccessField && (
                        <div className={`${styles.infoItem} ${theme === 'dark' ? styles.darkInfoItem : ''}`}>
                            <div className={`${styles.labelBox} ${theme === 'dark' ? styles.darkLabelBox : ''}`}>{currentTranslation.tradingAccess}</div>
                            <div className={styles.displayBox}>
                                <span className={`${styles.displayValue} ${theme === 'dark' ? styles.darkDisplayValue : ''}`}>
                                    {leadData.trading_access_blocked ? currentTranslation.tradingAccessBlocked : currentTranslation.tradingAccessAllowed}
                                </span>
                                {errors.trading_access && (
                                    <div className={styles.switchError}>{errors.trading_access}</div>
                                )}
                            </div>
                            <div className={styles.actionBox}>
                                {canEditTradingAccess && (
                                    <button
                                        type="button"
                                        className={`${styles.tradingAccessSwitch} ${leadData.trading_access_blocked ? styles.tradingAccessSwitchBlocked : styles.tradingAccessSwitchAllowed} ${theme === 'dark' ? styles.darkTradingAccessSwitch : ''}`}
                                        onClick={handleTradingAccessToggle}
                                        disabled={isTradingAccessSubmitting}
                                        aria-label={currentTranslation.tradingAccess}
                                    >
                                        <span className={`${styles.tradingAccessThumb} ${leadData.trading_access_blocked ? styles.tradingAccessThumbBlocked : ''}`} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {groupedFields.config.map(renderField)}
                </div>
            )}

            {activeTab === 'kycSales' && hasKycSalesFields && (
                <KycSalesData
                    fields={groupedFields.kycSales}
                    leadData={leadData}
                    renderField={renderField}
                />
            )}

            {activeTab === 'kycRetention' && hasKycRetentionFields && (
                <KycRetentionData
                    fields={groupedFields.kycRetention}
                    leadData={leadData}
                    renderField={renderField}
                />
            )}


            {(activeTab === 'kycSales' && !hasKycSalesFields) && (
                <div className={infoGridClass}>
                    <div className={`${styles.noDataMessage} ${theme === 'dark' ? styles.darkNoDataMessage : ''}`}>{currentTranslation.noData}</div>
                </div>
            )}
            {(activeTab === 'kycRetention' && !hasKycRetentionFields) && (
                <div className={infoGridClass}>
                    <div className={`${styles.noDataMessage} ${theme === 'dark' ? styles.darkNoDataMessage : ''}`}>{currentTranslation.noData}</div>
                </div>
            )}
            {(activeTab === 'config' && !hasConfigSection) && (
                <div className={infoGridClass}>
                    <div className={`${styles.noDataMessage} ${theme === 'dark' ? styles.darkNoDataMessage : ''}`}>{currentTranslation.noData}</div>
                </div>
            )}


            {isOpenModal && (
                <LeadSettings
                    role={role}
                    setIsOpenModal={setIsOpenModal}
                    modalData={modalData}
                    onSave={handleSaveSuccess}
                />
            )}
        </div>
    );
};
